import { FC, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DefaultInput from "@/components/forms/inputs/defaultInput";

export interface KeyValueListBuilderProps {
  value?: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  label?: string;
  hint?: string;
  addButtonLabel?: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  sanitizeKey?: (rawKey: string) => string;
}

interface Row {
  id: string;
  key: string;
  value: string;
}

let rowIdCounter = 0;
const nextRowId = () => `kv_${rowIdCounter++}`;

const objectToRows = (obj?: Record<string, any>): Row[] => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return [];
  }
  return Object.entries(obj).map(([key, value]) => ({
    id: nextRowId(),
    key,
    value:
      value == null ? "" : typeof value === "string" ? value : String(value),
  }));
};

const rowsToObject = (rows: Row[]): Record<string, any> =>
  rows.reduce((acc, row) => {
    const trimmedKey = row.key.trim();
    if (trimmedKey !== "") {
      acc[trimmedKey] = row.value;
    }
    return acc;
  }, {} as Record<string, any>);

const KeyValueListBuilder: FC<KeyValueListBuilderProps> = ({
  value,
  onChange,
  label,
  hint,
  addButtonLabel,
  keyPlaceholder,
  valuePlaceholder,
  sanitizeKey,
}) => {
  const [rows, setRows] = useState<Row[]>(() => objectToRows(value));

  const emit = (newRows: Row[]) => {
    setRows(newRows);
    onChange(rowsToObject(newRows));
  };

  const handleAdd = () =>
    setRows((prev) => [...prev, { id: nextRowId(), key: "", value: "" }]);

  const handleKeyChange = (id: string, rawKey: string) => {
    const key = sanitizeKey ? sanitizeKey(rawKey) : rawKey;
    emit(rows.map((row) => (row.id === id ? { ...row, key } : row)));
  };

  const handleValueChange = (id: string, val: string) =>
    emit(rows.map((row) => (row.id === id ? { ...row, value: val } : row)));

  const handleDelete = (id: string) =>
    emit(rows.filter((row) => row.id !== id));

  return (
    <Box sx={{ mt: 1 }}>
      {label && <Typography variant="subtitle2">{label}</Typography>}
      {hint && (
        <Typography variant="caption" color="text.secondary" display="block">
          {hint}
        </Typography>
      )}
      {rows.map((row) => (
        <Box
          key={row.id}
          sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}
        >
          <DefaultInput
            width="100%"
            style={{ flex: 1 }}
            placeholder={keyPlaceholder}
            value={row.key}
            onChange={(e) => handleKeyChange(row.id, e.target.value)}
          />
          <DefaultInput
            width="100%"
            style={{ flex: 1 }}
            placeholder={valuePlaceholder}
            value={row.value}
            onChange={(e) => handleValueChange(row.id, e.target.value)}
          />
          <IconButton
            aria-label="delete"
            color="error"
            onClick={() => handleDelete(row.id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 1 }}>
        {addButtonLabel}
      </Button>
    </Box>
  );
};

export default KeyValueListBuilder;
